import React, {useState, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory, useLocation} from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';

import Instructions from './Instructions';
import SnackbarSuccess from './SnackbarSuccess';
import AutoCompleteTags from './AutoCompleteTags';
import {BluePillButton} from '../../common/components/BluePillButton';
import {APP_WORD_WORK, FEATURES} from '../../constants';
import {createExercise, updateExercise} from '../../redux/slices/builder';
import {selectUserGroup} from '../../redux/slices/user';

import baseStyles from '../../css/Base.module.css';

export default function CreateEditWordWorkExercise({exercise}) {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const [mode, setMode] = useState(null); // CREATE, EDIT
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [isEditorDialogVisible, toggleIsEditorDialogVisible] = useState(false);
  const [isSnackbarOpen, toggleSnackbar] = useState(false);
  const [isSaving, toggleIsSaving] = useState(false);
  const [name, setName] = useState(null);
  const [description, setDescription] = useState(null);
  const [visibility, setVisibility] = useState('');
  const [tags, setTags] = useState([]);
  const [instructionsHTML, setInstructionsHTML] = useState(null);
  const [instructionsPlainText, setInstructionsPlainText] = useState(null);

  const userGroup = useSelector(selectUserGroup);
  const {
    tier: {features},
  } = userGroup;
  const visibilityFeatures = features.filter((feature) => feature.startsWith('VISIBILITY_'));

  useEffect(() => {
    if (!exercise) {
      setMode('CREATE');
      setVisibility(getDefaultVisibility());
    } else {
      setMode('EDIT');
      setExistingExerciseValues();
    }
  }, [exercise?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  function getDefaultVisibility() {
    // Auto set visibility when only one option is available
    if (visibilityFeatures.length === 1) {
      if (visibilityFeatures[0] === FEATURES.VISIBILITY_PRIVATE) return 'PRIVATE';
      if (visibilityFeatures[0] === FEATURES.VISIBILITY_USER_GROUP) return 'USER_GROUP';
      if (visibilityFeatures[0] === FEATURES.VISIBILITY_PUBLIC) return 'PUBLIC';
    } else if (visibilityFeatures.includes(FEATURES.VISIBILITY_PRIVATE)) return 'PRIVATE';
    return '';
  }

  function setExistingExerciseValues() {
    setName(exercise.name);
    setDescription(exercise.description);
    setVisibility(exercise.visibility);
    setTags(exercise.tags);
    setInstructionsHTML(exercise?.instructions?.html);
    setInstructionsPlainText(exercise?.instructions?.plainText);
  }

  function handleNameChange(event) {
    setName(event.target.value);
  }

  function handleVisibilityChanged(event) {
    setVisibility(event.target.value);
  }

  function isSaveButtonEnabled() {
    return !!name && !!visibility && !isSaving;
  }

  async function handleSave(exercisePayload) {
    if (mode === 'CREATE') {
      const newExercise = await dispatch(
        createExercise(
          exercisePayload.name,
          exercisePayload.type,
          exercisePayload.description,
          exercisePayload.visibility,
          {},
          exercisePayload.tags,
        ),
      );
      history.push(`/builder/exercise/${newExercise._id}`); // Go into "edit" mode
    } else if (mode === 'EDIT') {
      await dispatch(updateExercise(exercise._id, exercisePayload));
    }

    toggleSnackbar(true);
    toggleIsSaving(false);
  }

  function handleClickCancel() {
    history.push(`/builder/exercises?type=${APP_WORD_WORK}`);
  }

  async function handleClickSave() {
    toggleIsSaving(true);

    const tagIDs = tags.map((tag) => tag._id);

    const exercisePayload = {
      type: APP_WORD_WORK,
      name,
      description,
      visibility,
      tags: tagIDs,
    };
    handleSave(exercisePayload);
  }

  function renderTabDetails() {
    return (
      <>
        <div className="p-lr-15 p-b-15">
          <Typography variant="subtitle1" className="clr-dark-blue fnt-bold">
            Basic Info
          </Typography>

          <Grid container>
            <Grid item xs={12} md={6} xl={4}>
              <TextField
                value={name ?? ''}
                onChange={handleNameChange}
                label="Name"
                variant="standard"
                autoFocus
                fullWidth
                required
              />
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={12} md={6} xl={4}>
              <TextField
                variant="standard"
                className="m-t-10"
                value={description ?? ''}
                onChange={(event) => setDescription(event.target.value)}
                label="Description"
                multiline
                fullWidth
              />
            </Grid>
          </Grid>

          {visibilityFeatures.length > 1 && (
            <div className="m-t-30">
              <Typography variant="subtitle1" className="clr-dark-blue fnt-bold">
                Visibility
              </Typography>
              <Typography variant="body2" className="fnt-bold">
                You may keep this lesson for private use, or share for others to use.
              </Typography>
              <FormControl>
                <RadioGroup value={visibility} onChange={handleVisibilityChanged}>
                  {features.includes(FEATURES.VISIBILITY_PRIVATE) && (
                    <FormControlLabel value="PRIVATE" control={<Radio />} label="Private" />
                  )}
                  {features.includes(FEATURES.VISIBILITY_USER_GROUP) && (
                    <FormControlLabel value="USER_GROUP" control={<Radio />} label="Everyone" />
                  )}
                  {features.includes(FEATURES.VISIBILITY_PUBLIC) && (
                    <FormControlLabel value="PUBLIC" control={<Radio />} label="Public" />
                  )}
                </RadioGroup>
              </FormControl>
            </div>
          )}

          <Grid container className="m-t-30">
            <Grid item xs={12} md={6} xl={4}>
              <Typography variant="subtitle1" className="clr-dark-blue fnt-bold">
                Tags
              </Typography>
              <Typography variant="body2" className="fnt-bold">
                Create a tag system to organize and find files and other information faster.
              </Typography>
              <AutoCompleteTags tags={tags} setTags={setTags} />
            </Grid>
          </Grid>
        </div>
        <AppBar position="fixed" className="bg-gray-100" sx={{top: 'auto', bottom: 0}}>
          <Toolbar>
            <Grid container direction="row" justifyContent="center" alignItems="center">
              <Button className={baseStyles['btn-pill']} onClick={handleClickCancel}>
                Cancel
              </Button>
              <BluePillButton variant="outlined" disabled={!isSaveButtonEnabled()} onClick={handleClickSave}>
                {isSaving ? <CircularProgress size={25} /> : 'Save'}
              </BluePillButton>
            </Grid>
          </Toolbar>
        </AppBar>
        <Toolbar /> {/* Makes AppBar at the bottom of the page */}
      </>
    );
  }

  function handleNavigateToEditor() {
    history.push(
      `/exercise/${exercise._id}?return=${encodeURIComponent(location.pathname + location.search)}&edit=true`,
    );
  }

  function isEditorTabEnabled() {
    return mode === 'EDIT';
  }

  function renderTabEditor() {
    return (
      <Dialog fullWidth maxWidth="xs" open={isEditorDialogVisible} onClose={() => toggleIsEditorDialogVisible(false)}>
        <DialogTitle>Word Work Editor</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" className="fnt-bold">
            Redirect to Drag&apos;n Drop Word Work Editor
          </Typography>
          <Typography variant="subtitle1">To continue, press the "Go" button below.</Typography>
          <Typography variant="subtitle1" className="m-t-10">
            When you&apos;re finished editing this exercise, click "SAVE THIS EXERCISE".
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => toggleIsEditorDialogVisible(false)}>CANCEL</Button>
          <BluePillButton variant="outlined" onClick={handleNavigateToEditor}>
            GO
          </BluePillButton>
        </DialogActions>
      </Dialog>
    );
  }

  function handleChangeTab(event, newTabIndex) {
    if (newTabIndex === 2) toggleIsEditorDialogVisible(true);
    else setActiveTabIndex(newTabIndex);
  }

  async function handleSaveInstructions() {
    await dispatch(
      updateExercise(exercise._id, {instructions: {html: instructionsHTML, plainText: instructionsPlainText}}),
    );
    toggleSnackbar(true);
  }

  function renderTabInstructions() {
    return (
      <Instructions
        instructionsHTML={instructionsHTML}
        setInstructionsHTML={setInstructionsHTML}
        setInstructionsPlainText={setInstructionsPlainText}
        handleClickCancel={handleClickCancel}
        handleSaveInstructions={handleSaveInstructions}
      />
    );
  }

  return (
    <>
      <SnackbarSuccess text="Exercise Saved." isSnackbarOpen={isSnackbarOpen} toggleSnackbar={toggleSnackbar} />
      {renderTabEditor()}
      <Tabs
        className="m-b-30"
        value={activeTabIndex}
        onChange={handleChangeTab}
        indicatorColor="primary"
        textColor="primary"
      >
        <Tab label="DETAILS" />
        <Tab label="INSTRUCTIONS" disabled={mode === 'CREATE'} />
        <Tab label="WORD WORK EDITOR" disabled={!isEditorTabEnabled()} />
      </Tabs>

      {activeTabIndex === 0 && renderTabDetails()}
      {activeTabIndex === 1 && renderTabInstructions()}
    </>
  );
}
